import { Grid, Stack } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { useState } from "react";
import { MaterialGroupName } from "src/store/api/generatedApi";
import { MultiSelect } from "../../common/inputs/multiSelect";
import { SelectBox } from "../Selectbox";
import { TableHeader } from "../TableHeader";
import { ToggleSelector } from "../ToggleSelector";
import { defaultMaterialGrouping } from "../materialGroupings";
import { GroupedInventorySeries, InventoryPlotType } from "../types";
import { InventoryPlot } from "./InventoryPlot";
import { InventoryTable } from "./InventoryTable";
import { latestInventoryDate } from "./inventoryHooks";
import { DateRangeSelector } from "components/common/DateRangeSelector";

import { useStringQueryParamState } from "hooks/useStringQueryParamState";
import { useLiteralListQueryParamState } from "hooks/useLiteralListQueryParamState";
import { useDateRange } from "contexts/DateRangeContext";
import { useTenant } from "hooks/settings";
import { ExcelDownloadButton } from "components/common/ExcelDownloadButton";
import { backendApi, downloadAsExcel } from "src/store/api/enhancedApi";

interface InventoryTabProps {
  groupedInventorySeries: GroupedInventorySeries;
}

export const InventoryTab = ({ groupedInventorySeries }: InventoryTabProps) => {
  const { t } = useTenantTranslation();
  const { startDate, endDate } = useDateRange();
  const tenantName = useTenant();

  const [selectedGrouping, setSelectedGrouping] = useStringQueryParamState(
    "grouping",
    defaultMaterialGrouping(groupedInventorySeries.availableMaterialGroupings)
  );

  const [selectedPlotType, setSelectedPlotType] =
    useStringQueryParamState<InventoryPlotType>("plotType", "mass");

  const materialGroupsInSelectedGrouping = selectedGrouping
    ? (groupedInventorySeries.availableMaterialGroupings[selectedGrouping] ??
      [])
    : [];

  const [selectedMaterials, setSelectedMaterials] =
    useLiteralListQueryParamState<MaterialGroupName>({
      paramName: "materialGroups",
      permitted: materialGroupsInSelectedGrouping,
      localStorageKey: `${tenantName}.strategyDashboard.inventory.selectedMaterials`,
      defaultValue: materialGroupsInSelectedGrouping,
    });

  const visibleMaterials =
    selectedMaterials.length > 0
      ? selectedMaterials
      : materialGroupsInSelectedGrouping;

  const [selectedDate, setSelectedDate] = useState(() =>
    latestInventoryDate(groupedInventorySeries)
  );

  const start = startDate.format();
  const end = endDate.format();

  const [downloadInventory] =
    backendApi.endpoints.getStrategyDashboardInventory.useLazyQuery();
  const getInventoryExcelBlob = () =>
    downloadAsExcel(downloadInventory, {
      tenantName,
      start,
      end,
      "Cache-Control": "no-cache",
    });

  return (
    <div>
      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-between"
        sx={{ mt: 2 }}
      >
        <DateRangeSelector />
        <ExcelDownloadButton
          variant="outlined"
          getBlob={getInventoryExcelBlob}
        />
      </Stack>
      <Grid
        container
        spacing={2}
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          mt: 1,
        }}
      >
        <Grid>
          <SelectBox
            availableOptions={Object.keys(
              groupedInventorySeries.availableMaterialGroupings
            )}
            selectedOption={selectedGrouping}
            onOptionChange={setSelectedGrouping}
            id="inventory-grouping-select"
            labelId="inventory-grouping-label"
            label={t("materialGrouping")}
          />
        </Grid>
        <Grid>
          <ToggleSelector<InventoryPlotType>
            title="plotType"
            selectedValue={selectedPlotType}
            setSelectedValue={setSelectedPlotType}
            availableValueLabels={{ mass: "mass", fraction: "fraction" }}
          />
        </Grid>
        <Grid>
          <MultiSelect<MaterialGroupName, MaterialGroupName>
            sx={{ minWidth: 240 }}
            options={materialGroupsInSelectedGrouping}
            getOptionId={(materialGroup) => materialGroup}
            getOptionLabel={(materialGroup) => t(materialGroup)}
            values={selectedMaterials}
            setValues={setSelectedMaterials}
            small
            selectAll
            labelAbove
            label={t("materialGroups")}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1} mt={2} mx={0}>
        <Grid size={{ xs: 8 }} ml={0}>
          <InventoryPlot
            inventorySeries={groupedInventorySeries}
            selectedGrouping={selectedGrouping}
            selectedPlotType={selectedPlotType}
            selectedMaterials={visibleMaterials}
            setSelectedDate={setSelectedDate}
          />
        </Grid>
        <Grid size={{ xs: 4 }}>
          <TableHeader
            titlePrefix={t("inventoryOn")}
            subtitleText={t("inventoryTableSubtitleText")}
            selectedDate={selectedDate}
            formatDateMonthly={false}
          />
          <InventoryTable
            inventorySeries={groupedInventorySeries}
            selectedDate={selectedDate}
            selectedMaterialGrouping={selectedGrouping}
          />
        </Grid>
      </Grid>
    </div>
  );
};
